import { Box, useTheme } from '@mui/material';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';

import { Icon, Row, Text } from '@/components/ui';
import { ConfirmModal } from '@/components/common';
import { backArrow, deleteIconContainerRed } from '@/assets';

interface HeaderProps {
  title: string;
  hideBackButton?: boolean;
  linkBack?: string;
  subTitle?: string;
  onBack?: () => void;
  withConfirm?: boolean;
  handleDelete?: () => void;
}

export const Header: FC<HeaderProps> = ({
  title,
  hideBackButton,
  linkBack,
  subTitle,
  withConfirm,
  onBack,
  handleDelete,
}) => {
  const { direction } = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showModalConfirm, setShowModalConfirm] = useState<boolean>(false);
  const isRtl = direction === 'rtl';

  const handleBackButton = () => {
    if (onBack) {
      onBack();
      return;
    }

    if (withConfirm) {
      setShowModalConfirm(true);
    } else {
      handleBack();
    }
  };

  const handleBack = () => {
    if (linkBack) {
      navigate({ to: linkBack });
    } else {
      navigate({ to: '..' });
    }
  };

  return (
    <>
      <Box width='100%' sx={{ ml: '-2px', direction: !isRtl ? 'ltr' : 'rtl' }}>
        <Box sx={{ height: '4rem', maxWidth: { xs: '100%', sm: 900 } }}></Box>
        <Row
          sx={{
            py: 1,
            paddingTop: '1rem',
            justifyContent: 'space-between',
            position: 'fixed',
            zIndex: '200',
            background: 'white',
            width: '100%',
            top: 0,
            cursor: 'pointer',
            maxWidth: { xs: '100%', sm: 900 },
          }}
        >
          {hideBackButton ? (
            <Box
              sx={{
                height: '2.5rem',
                display: 'flex',
              }}
            />
          ) : (
            <Box
              sx={{
                width: '2.5rem',
                height: '2.5rem',
                borderRadius: '50%',
                backgroundColor: '#F5F5F5',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
              onClick={handleBackButton}
            >
              <Icon src={backArrow} />
            </Box>
          )}
          <Box width={'calc(100% - 5rem)'}>
            <Text
              sx={{
                color: 'primary.dark',
                fontSize: '1.25rem',
                fontWeight: 'bold',
                maxWidth: '100%',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                direction: 'ltr',
                textAlign: 'center',
              }}
            >
              <span>{subTitle ? subTitle : ''}</span> <span>{title}</span>
            </Text>
          </Box>
          {handleDelete ? (
            <Box
              onClick={handleDelete}
              sx={{
                width: '2.5rem',
                height: '2.5rem',
                borderRadius: '50%',
                backgroundColor: '#F5F5F5',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: 'pointer',
                position: 'absolute',
                left: isRtl ? '2rem' : 'auto',
                right: isRtl ? 'auto' : '2rem',
                zIndex: 20,
              }}
            >
              <Icon src={deleteIconContainerRed} />
            </Box>
          ) : (
            <></>
          )}
          <Box
            sx={{
              width: '1.75rem',
              height: '1.75rem',
              borderRadius: '50%',
              backgroundColor: 'primary.dark',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              opacity: 0,
              minWidth: '4.75rem',
            }}
          >
            <Text fontSize={'0.75rem'}>AZ</Text>
          </Box>
        </Row>
      </Box>
      <ConfirmModal
        cancel={() => setShowModalConfirm(false)}
        confirm={() => {
          setShowModalConfirm(false);
          handleBack();
        }}
        title={t('leave_without_saving_changes')}
        okText={t('yes_confirm_modal')}
        show={showModalConfirm}
      />
    </>
  );
};
